// Registry of "drop your cached data" callbacks owned by composables that
// keep module-level singletons (e.g. useNotifications, useForexTickers).
// useSession.kill() calls every registered callback on logout so a re-login
// as a different account starts from a clean slate. Composables register
// at module-eval time; there is no unregister API.

import * as Sentry from "@sentry/browser";

type ResetFn = () => void;

const resetCallbacks: ResetFn[] = [];

export const registerSharedCacheReset = (fn: ResetFn) => {
  resetCallbacks.push(fn);
};

// Every callback is wrapped so a buggy reset in one composable cannot
// stop the others from running. Throwing here would also break logout
// (kill() is the only caller and a throw would prevent webAuth.logout()
// from being reached). Failures are reported to Sentry so a silently
// failing reset — which would leak the previous user's data on next
// login — is visible in production.
export const resetSharedCaches = () => {
  for (const fn of resetCallbacks) {
    try {
      fn();
    } catch (e) {
      Sentry.captureException(e, {
        tags: { component: "sharedCacheReset", action: "reset_callback" },
      });
      console.error("resetSharedCaches: a reset callback threw", e);
    }
  }
};

// Test-only: wipe the registry so unit tests can verify the registration
// contract in isolation. Not used at runtime — exported under an obviously
// test-shaped name so accidental production use stands out in review.
export const __resetRegistryForTests = () => {
  resetCallbacks.length = 0;
};
